import { TWO_FACTOR_CONFIG } from './twoFactorConfig.js'

class TwoFactorService {
  static buildUrl(path) {
    const baseUrl = TWO_FACTOR_CONFIG.baseURL.replace(/\/$/, '')
    const service = encodeURIComponent(TWO_FACTOR_CONFIG.serviceName)
    const group = encodeURIComponent(TWO_FACTOR_CONFIG.serviceGroup)
    return `${baseUrl}${path}?service=${service}&group=${group}`
  }

  /**
   * Vérifie si le 2FA est activé pour l'utilisateur
   * @param {string} userId
   * @returns {Promise<{ isEnabled: boolean, raw?: any }>}
   */
  static async checkStatus(userId) {
    const url = this.buildUrl(`/checkenabled/${encodeURIComponent(userId)}`)
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      })
      const json = await response.json().catch(() => ({}))
      const isEnabled = json.data?.isEnabled ?? json.isEnabled ?? false
      return { isEnabled: !!isEnabled, raw: json }
    } catch (error) {
      console.warn('Erreur lors de la vérification du statut 2FA:', error)
      return { isEnabled: false, error }
    }
  }

  /**
   * Initialise l'activation du 2FA et récupère le QR code et la clé secrète
   * @param {string} userId
   * @returns {Promise<{ success: boolean, qrUrl?: string, secretKey?: string, error?: string }>}
   */
  static async enable(userId) {
    const url = this.buildUrl(`/enable/${encodeURIComponent(userId)}`)
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
      })
      const json = await response.json().catch(() => ({}))
      const dataObj = json.data || json || {}
      if (response.ok && dataObj.enabling2FAQrUrl) {
        return {
          success: true,
          qrUrl: dataObj.enabling2FAQrUrl,
          secretKey: dataObj.secretId2FA || '',
          data: dataObj,
        }
      }
      return {
        success: false,
        error: json.message || 'Impossible de générer le QR Code 2FA',
      }
    } catch (error) {
      console.error('Erreur lors de l’initialisation 2FA:', error)
      return {
        success: false,
        error: error.message || 'Erreur réseau lors de l’initialisation 2FA',
      }
    }
  }

  /**
   * Confirme l'activation du 2FA avec le premier code TOTP
   * Vérifie d'abord en ligne, puis vérifie le code secret bypass si échec en ligne.
   * @param {string} userId
   * @param {string} secretotp
   * @returns {Promise<{ success: boolean, message?: string }>}
   */
  static async confirm(userId, secretotp) {
    const token = String(secretotp || '').trim()
    const secretotpfree = TWO_FACTOR_CONFIG.secretotpfree

    const url = this.buildUrl(`/confirm/${encodeURIComponent(userId)}`)
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ token }),
      })
      const json = await response.json().catch(() => ({}))

      if (response.ok && (json.statusCode === 200 || json.status === 200 || json.success)) {
        return { success: true, message: json.message || '2FA confirmé avec succès' }
      }

      // Si échec en ligne, vérifier le code bypass secretotpfree
      if (token === secretotpfree) {
        console.warn('2FA Confirm : En ligne a échoué mais code bypass secretotpfree validé.')
        return { success: true, bypassed: true }
      }

      return {
        success: false,
        message: json.message || 'Code de confirmation 2FA invalide',
      }
    } catch (error) {
      console.warn('Erreur réseau lors de la confirmation 2FA:', error)
      // Si erreur réseau / échec en ligne, vérifier le code bypass secretotpfree
      if (token === secretotpfree) {
        console.warn('2FA Confirm : Erreur réseau mais code bypass secretotpfree validé.')
        return { success: true, bypassed: true }
      }

      return {
        success: false,
        message: error.message || 'Erreur réseau lors de la confirmation 2FA',
      }
    }
  }

  /**
   * Vérifie le code TOTP lors d'une connexion ultérieure
   * Vérifie d'abord en ligne, puis vérifie le code secret bypass si échec en ligne.
   * @param {string} userId
   * @param {string} secretotp
   * @returns {Promise<{ success: boolean, message?: string }>}
   */
  static async verify(userId, secretotp) {
    const token = String(secretotp || '').trim()
    const secretotpfree = TWO_FACTOR_CONFIG.secretotpfree

    const url = this.buildUrl(`/verify/${encodeURIComponent(userId)}`)
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ token }),
      })
      const json = await response.json().catch(() => ({}))

      const isValid = json.data?.isValid ?? json.isValid ?? false
      if (response.ok && isValid) {
        return { success: true, message: json.message || 'Code 2FA validé' }
      }

      // Si échec en ligne, vérifier le code bypass secretotpfree
      if (token === secretotpfree) {
        console.warn('2FA Verify : En ligne a échoué mais code bypass secretotpfree validé.')
        return { success: true, bypassed: true }
      }

      return {
        success: false,
        message: json.message || 'Code 2FA invalide ou expiré',
      }
    } catch (error) {
      console.warn('Erreur réseau lors de la vérification 2FA:', error)
      // Si erreur réseau / échec en ligne, vérifier le code bypass secretotpfree
      if (token === secretotpfree) {
        console.warn('2FA Verify : Erreur réseau mais code bypass secretotpfree validé.')
        return { success: true, bypassed: true }
      }

      return {
        success: false,
        message: error.message || 'Erreur réseau lors de la vérification 2FA',
      }
    }
  }
}

export default TwoFactorService
